import { useCallback, useMemo } from "react";
import type { IFormulaEditState, ITableCell } from "../types";
import { expandRange } from "../utils/coord-translation";
import { formulaEditTargetsCell } from "../utils/formula-edit";
import { parseFormula } from "../utils/parse-formula";

export interface IFormulaCellHighlight {
  // Formula-reference (blue) perimeter edges.
  formulaCrossingTop: boolean;
  formulaCrossingBottom: boolean;
  formulaCrossingLeft: boolean;
  formulaCrossingRight: boolean;
  // Calc/edit-target (green) perimeter edges.
  formulaCrossingGreenTop: boolean;
  formulaCrossingGreenBottom: boolean;
  formulaCrossingGreenLeft: boolean;
  formulaCrossingGreenRight: boolean;
  // Live buffer for the cell being formula-edited; undefined for every other cell.
  formulaEditBuffer: string | undefined;
  isFormulaReferenced: boolean;
}

interface IProps {
  entityId: string;
  tableData: ITableCell[];
  tableCols: number;
  tableRows: number;
  // The calc cell whose formula is being authored (editorCtx.startCellId).
  startCellId?: string;
  // Formula-edit state, injected from editorData (keeps @library store-free).
  formulaEdit: IFormulaEditState | null;
}

/**
 * Formula-edit highlighting: which cells/edges light up while a formula is being
 * authored — blue for referenced cells, green for the calc/edit target — plus
 * the live buffer for the editing cell. All editor-only (absent in the reader).
 * Returns a per-cell accessor consumed inside the TableRows render loop.
 */
export const useFormulaHighlight = ({
  entityId,
  tableData,
  tableCols,
  tableRows,
  startCellId,
  formulaEdit,
}: IProps) => {

  const referencedCellIds = useMemo<Set<string> | null>(() => {
    // Scope to the owning table — cell ids repeat across tables.
    if (!formulaEdit || formulaEdit.entityId !== entityId) return null;
    const parsed = parseFormula(formulaEdit.buffer);
    if (!parsed.ok) return null;
    const ids = new Set<string>();
    for (const r of parsed.refs) {
      for (const c of expandRange(r.start, r.end)) {
        if (c.col < 0 || c.col >= tableCols) continue;
        if (c.row < 0 || c.row >= tableRows) continue;
        const refCell = tableData[c.row * tableCols + c.col];
        if (refCell) ids.add(refCell.id);
      }
    }
    return ids;
  }, [formulaEdit, entityId, tableData, tableCols, tableRows]);

  const getFormulaHighlight = useCallback(
    (cell: ITableCell, row: number, col: number): IFormulaCellHighlight => {
      const idAt = (r: number, c: number) =>
        r >= 0 && r < tableRows && c >= 0 && c < tableCols
          ? tableData[r * tableCols + c]?.id
          : undefined;
      const cellAt = (r: number, c: number) =>
        r >= 0 && r < tableRows && c >= 0 && c < tableCols
          ? tableData[r * tableCols + c]
          : undefined;

      // Formula-reference highlight (blue): an edge participates if either
      // cell touching it is referenced by the formula being edited.
      const ref = (id: string | undefined) =>
        !!id && !!referencedCellIds?.has(id);
      const cellRef = ref(cell.id);
      const belowRef = ref(idAt(row + 1, col));
      const rightRef = ref(idAt(row, col + 1));

      // Green highlight: the calc cell being authored / the formula-edit target.
      const isGreenWearer = (c: ITableCell | undefined): boolean => {
        if (!c) return false;
        if (formulaEditTargetsCell(formulaEdit, entityId, c.id)) return true;
        if (c.calculation && startCellId === c.id) return true;
        return false;
      };
      const cellGreen = isGreenWearer(cell);
      const belowGreen = isGreenWearer(cellAt(row + 1, col));
      const rightGreen = isGreenWearer(cellAt(row, col + 1));

      // Only the editing cell receives the buffer so others' memo bail-outs hold.
      const formulaEditBuffer = formulaEditTargetsCell(
        formulaEdit,
        entityId,
        cell.id,
      )
        ? formulaEdit?.buffer
        : undefined;

      return {
        formulaCrossingTop: cellRef,
        formulaCrossingBottom: cellRef || belowRef,
        formulaCrossingLeft: cellRef,
        formulaCrossingRight: cellRef || rightRef,
        formulaCrossingGreenTop: cellGreen,
        formulaCrossingGreenBottom: cellGreen || belowGreen,
        formulaCrossingGreenLeft: cellGreen,
        formulaCrossingGreenRight: cellGreen || rightGreen,
        formulaEditBuffer,
        isFormulaReferenced: referencedCellIds?.has(cell.id) ?? false,
      };
    },
    [
      referencedCellIds,
      formulaEdit,
      entityId,
      tableData,
      tableCols,
      tableRows,
      startCellId,
    ],
  );

  return { getFormulaHighlight };
};
